import type {
  AdminDashboardSummary,
  AdminDashboardTodayData,
} from "@/admin/entities/adminDashboard";
import {
  computeTrend,
  formatUsd,
  pluralRu,
  trendTone,
  type TrendPolarity,
  type TrendTone,
  type UnpaidSummary,
} from "./dashboardFormat";

export interface FinancialSummaryRow {
  key: string;
  label: string;
  current: number;
  /** null у строк «на сегодня» и без периода сравнения. */
  previous: number | null;
  change: number | null;
  tone: TrendTone;
  /** Итоговые строки жирным. */
  emphasis: boolean;
  negative: boolean;
  note: string | null;
  /** Значение на сегодня, сравнения нет. */
  snapshot: boolean;
}

export interface FinancialSummaryInput {
  current: AdminDashboardSummary;
  previous: AdminDashboardSummary | null | undefined;
  unpaid: UnpaidSummary;
  today: AdminDashboardTodayData;
}

interface PeriodRowDefinition {
  key: string;
  label: string;
  pick: (summary: AdminDashboardSummary) => number;
  polarity: TrendPolarity;
  emphasis?: boolean;
}

const BEFORE_SNAPSHOT: PeriodRowDefinition[] = [
  {
    key: "sales",
    label: "Продажи клиентов",
    pick: (s) => s.salesUSD,
    polarity: "higherIsBetter",
  },
  {
    key: "revenueShare",
    label: "Наш ревшер (начислено)",
    pick: (s) => s.revenueShareUSD,
    polarity: "higherIsBetter",
  },
  {
    key: "licenses",
    label: "Лицензии за период",
    pick: (s) => s.licensesAccruedUSD,
    polarity: "higherIsBetter",
  },
  {
    key: "earned",
    label: "Заработано за период",
    pick: (s) => s.earnedUSD,
    polarity: "higherIsBetter",
    emphasis: true,
  },
  {
    key: "issued",
    label: "Выставлено счетов",
    pick: (s) => s.issuedInvoicesUSD,
    polarity: "higherIsBetter",
  },
  {
    key: "paid",
    label: "Оплачено мне",
    pick: (s) => s.paidInvoicesUSD,
    polarity: "higherIsBetter",
  },
];

const AFTER_SNAPSHOT: PeriodRowDefinition[] = [
  {
    key: "expenses",
    label: "Траты",
    pick: (s) => s.expensesUSD,
    polarity: "higherIsWorse",
  },
  {
    key: "netIncome",
    label: "Чистый доход (деньги)",
    pick: (s) => s.netIncomeUSD,
    polarity: "higherIsBetter",
    emphasis: true,
  },
  {
    key: "accrualProfit",
    label: "Прибыль по начислению",
    pick: (s) => s.accrualProfitUSD,
    polarity: "higherIsBetter",
    emphasis: true,
  },
];

const INVOICE_FORMS: [string, string, string] = ["счёт", "счёта", "счетов"];

/** Пометка к «Ждём оплаты»: просрочка и счета без курса, не вошедшие в сумму. */
function unpaidNote(unpaid: UnpaidSummary): string {
  const overdue = `из них просрочено: ${formatUsd(unpaid.overdueUSD)}`;
  if (unpaid.withoutRateCount <= 0) return overdue;
  return `${overdue}; без курса: ${unpaid.withoutRateCount} ${pluralRu(unpaid.withoutRateCount, INVOICE_FORMS)}`;
}

/**
 * «Финансовая сводка» (спека §3.1a): лестница денег периода сверху вниз,
 * у каждой строки прошлый период и изменение; у строк «на сегодня»
 * сравнения нет. Итоги «Заработано», «Чистый доход» и «Прибыль по
 * начислению» жирные и красные при минусе.
 */
export function buildFinancialSummaryRows(
  input: FinancialSummaryInput
): FinancialSummaryRow[] {
  const { current, previous, unpaid, today } = input;

  const periodRow = (definition: PeriodRowDefinition): FinancialSummaryRow => {
    const value = definition.pick(current);
    const before = previous ? definition.pick(previous) : null;
    const change = computeTrend(value, before);
    return {
      key: definition.key,
      label: definition.label,
      current: value,
      previous: before,
      change,
      tone: trendTone(change, definition.polarity),
      emphasis: Boolean(definition.emphasis),
      negative: value < 0,
      note: null,
      snapshot: false,
    };
  };

  const snapshotRow = (
    key: string,
    label: string,
    value: number,
    note: string | null
  ): FinancialSummaryRow => ({
    key,
    label,
    current: value,
    previous: null,
    change: null,
    tone: "neutral",
    emphasis: false,
    negative: value < 0,
    note,
    snapshot: true,
  });

  return [
    ...BEFORE_SNAPSHOT.map(periodRow),
    snapshotRow("unpaid", "Ждём оплаты", unpaid.totalUSD, unpaidNote(unpaid)),
    snapshotRow(
      "pendingRevenueShare",
      "Ревшер к выставлению",
      today.pendingRevenueShareUSD,
      null
    ),
    ...AFTER_SNAPSHOT.map(periodRow),
  ];
}

export type FinancialSummaryGroupKey = "earned" | "money" | "result";

export interface FinancialSummaryGroup {
  key: FinancialSummaryGroupKey;
  title: string;
  rows: FinancialSummaryRow[];
}

const GROUPS: {
  key: FinancialSummaryGroupKey;
  title: string;
  rowKeys: string[];
}[] = [
  {
    key: "earned",
    title: "Заработано",
    rowKeys: ["sales", "revenueShare", "licenses", "earned"],
  },
  {
    key: "money",
    title: "Деньги",
    rowKeys: ["issued", "paid", "unpaid", "pendingRevenueShare"],
  },
  {
    key: "result",
    title: "Итог",
    rowKeys: ["expenses", "netIncome", "accrualProfit"],
  },
];

/**
 * Группы лестницы (спека 2026-10-16 §3.2): «Заработано», «Деньги», «Итог».
 * Порядок строк тот же, что у buildFinancialSummaryRows; группа без строк
 * не показывается.
 */
export function groupFinancialSummaryRows(
  rows: FinancialSummaryRow[]
): FinancialSummaryGroup[] {
  const byKey = new Map(rows.map((row) => [row.key, row]));
  return GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    rows: group.rowKeys.flatMap((key) => {
      const row = byKey.get(key);
      return row ? [row] : [];
    }),
  })).filter((group) => group.rows.length > 0);
}
